import { useCallback, useEffect, useState } from 'react';
import { isPosOfflineFeatureEnabled } from '../../offline/deviceIdentity';
import { saveCatalogSnapshot, getCatalogSnapshot } from '../../offline/storage/indexedDbPosStorage';
import {
  enqueueOfflineDraftTicket,
  listOutboxEntries,
  SYNC_STATE,
} from '../../offline/outbox';
import { syncPendingOutbox } from '../../offline/syncDraftTicket';

function useOnlineStatus() {
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

export default function PosOfflinePanel({ commercantId, catalogue, demoProduitId, demoProduitNom }) {
  const online = useOnlineStatus();
  const [entries, setEntries] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    setEntries(await listOutboxEntries());
  }, []);

  useEffect(() => {
    if (!isPosOfflineFeatureEnabled() || !commercantId) return;
    refresh();
  }, [commercantId, refresh]);

  useEffect(() => {
    if (!isPosOfflineFeatureEnabled() || !commercantId || !catalogue) return;
    if (online && catalogue?.produits?.length) {
      saveCatalogSnapshot(commercantId, catalogue).catch(() => {});
    }
  }, [online, commercantId, catalogue]);

  useEffect(() => {
    if (!isPosOfflineFeatureEnabled() || !online || syncing) return;
    const pending = entries.some((e) => e.syncState === SYNC_STATE.PENDING);
    if (pending) {
      (async () => {
        setSyncing(true);
        setMessage('Synchronisation en cours…');
        await syncPendingOutbox();
        await refresh();
        setSyncing(false);
        setMessage('Synchronisé');
      })();
    }
  }, [online, entries, syncing, refresh]);

  if (!isPosOfflineFeatureEnabled()) {
    return null;
  }

  const statusLabel = !online
    ? 'Hors connexion — brouillons locaux uniquement'
    : syncing
      ? 'Synchronisation en cours'
      : entries.some((e) => e.syncState === SYNC_STATE.PENDING)
        ? 'Brouillon local en attente'
        : entries.some((e) => e.syncState === SYNC_STATE.FAILED)
          ? 'Échec sync — action requise'
          : entries.some((e) => e.syncState === SYNC_STATE.ACKED)
            ? 'Brouillon synchronisé (pas de vente encaissée)'
            : 'Connecté';

  const handleSaveDraft = async () => {
    if (!demoProduitId) {
      setMessage('Catalogue local indisponible');
      return;
    }
    let snap = catalogue;
    if (!snap?.produits?.length) {
      snap = (await getCatalogSnapshot(commercantId))?.snapshot;
    }
    const produit =
      snap?.produits?.find((p) => p.id === demoProduitId) || snap?.produits?.[0];
    if (!produit) {
      setMessage('Chargez le catalogue en ligne une fois avant le mode hors ligne');
      return;
    }
    await enqueueOfflineDraftTicket({
      commercantId,
      offlineDraftLabel: `Brouillon offline ${demoProduitNom || produit.nom}`,
      draft: {
        produits: [{ produitId: produit.id, quantite: 1, options: {} }],
        commentaire: 'POS-L3-A1 brouillon local',
      },
    });
    setMessage('Brouillon enregistré localement (non encaissé)');
    await refresh();
  };

  return (
    <div
      className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
      data-testid="pos-offline-panel"
    >
      <div className="font-medium text-slate-800" data-testid="pos-offline-status">
        Offline POS (A1) — {statusLabel}
      </div>
      <p className="mt-1 text-xs text-slate-600">
        Brouillon local uniquement : aucun encaissement ni justificatif fiscal définitif.
      </p>
      {message ? <p className="mt-1 text-xs text-slate-700">{message}</p> : null}
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded bg-amber-600 px-3 py-1.5 text-white disabled:opacity-50"
          data-testid="pos-offline-save-draft"
          onClick={handleSaveDraft}
        >
          Enregistrer brouillon offline
        </button>
        <button
          type="button"
          className="rounded border border-slate-300 px-3 py-1.5 disabled:opacity-50"
          data-testid="pos-offline-sync-now"
          disabled={!online || syncing}
          onClick={async () => {
            setSyncing(true);
            await syncPendingOutbox();
            await refresh();
            setSyncing(false);
          }}
        >
          Synchroniser
        </button>
      </div>
      <ul className="mt-2 space-y-1">
        {[...entries].sort((a, b) => a.localSequence - b.localSequence).map((e) => (
          <li
            key={e.localId}
            data-testid="pos-offline-draft-row"
            data-sync-state={e.syncState}
            data-client-operation-id={e.clientOperationId}
          >
            #{e.localSequence} — {e.offlineDraftLabel} — {e.syncState}
            {e.serverTicketId ? ` → ticket ${e.serverTicketId}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}
